import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReviewQRDialog from "@/components/ReviewQRDialog";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !user) return;

    const verify = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("verify-payment", {
          body: { sessionId },
        });
        if (!error && data?.status === "paid") {
          setVerified(true);
          if (data.bookingId) setBookingId(data.bookingId);
          // Show the review QR popup shortly after success
          setTimeout(() => setReviewOpen(true), 1200);
          // Note: confirmation email + SMS are dispatched server-side by
          // verify-payment exactly once, on the first creation of the booking
          // (idempotent on stripe_session_id). Refreshing this page will not
          // resend notifications.
        }
      } catch (err) {
        console.error("Verification failed:", err);
      }
      setVerifying(false);
    };
    verify();
  }, [sessionId, user]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-lg text-center">
          {verifying ? (
            <div className="bg-card border border-border/50 p-10">
              <Loader2 size={48} className="animate-spin text-primary mx-auto mb-4" />
              <h1 className="font-serif text-3xl font-bold italic mb-2">Verifying Payment…</h1>
              <p className="text-muted-foreground font-light">Please wait while we confirm your payment.</p>
            </div>
          ) : verified ? (
            <div className="bg-card border border-primary/30 p-10">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-primary" />
              </div>
              <h1 className="font-serif text-3xl font-bold italic mb-2">Payment Confirmed!</h1>
              <p className="text-muted-foreground font-light mb-6">
                Your booking has been confirmed and payment received. You'll receive a confirmation email shortly.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/account")}
                  className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
                >
                  View My Appointments
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setReviewOpen(true)}
                  className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border-primary/40 hover:bg-primary/10"
                >
                  Leave a Review
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-destructive/30 p-10">
              <h1 className="font-serif text-3xl font-bold italic mb-2 text-destructive">Payment Issue</h1>
              <p className="text-muted-foreground font-light mb-6">
                We couldn't verify your payment. Your appointment has not been booked. Please try again or contact us.
              </p>
              <Button
                onClick={() => navigate("/book")}
                variant="outline"
                className="rounded-none px-10 border-primary/30 hover:bg-primary/10 tracking-[0.2em] uppercase text-xs font-medium"
              >
                Try Again
              </Button>
            </div>
          )}
        </div>
      </section>
      <ReviewQRDialog open={reviewOpen} onOpenChange={setReviewOpen} bookingId={bookingId} />
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
